import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    X, Search, Check, Library, Send, Inbox, CheckCircle2, AlertCircle,
    ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
// A factory, not a hook — same import style as MedicineMaster/ImagingProcedures.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import { MASTER_CATALOG_KINDS, emptyCatalogForm, catalogSubtitle } from '../../data/masterCatalogKinds';

// PICK FROM MASTER (Phase 2, decisions log #5) — one modal reused by the
// pharmacy / lab / radiology master screens. Deliberately plain axios, no
// redux: the three host pages fetch their lists three different ways, so the
// modal owns its own little world and just calls `onPicked()` when rows were
// added, letting each host refresh however it already does.
//
// Three tabs:
//   Browse    the world catalog, multi-select -> POST /catalog/:kind/pick.
//             Already-picked items come back as "skipped", shown as such.
//   Propose   the item is missing from the master -> send it up for review.
//             If the master already has it, the API answers ALREADY_IN_MASTER
//             and we surface "pick it instead".
//   Sent      this hospital's proposals for the kind, with review status.

const PAGE_SIZE = 8;

const PickFromMasterModal = ({ kind, open, onClose, onPicked }) => {
    const { t } = useTranslation('mastercatalog');
    const def = MASTER_CATALOG_KINDS[kind];

    const [tab, setTab] = useState('browse');
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [page, setPage] = useState(1);
    const [masterItems, setMasterItems] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [picking, setPicking] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const [proposeForm, setProposeForm] = useState(() => emptyCatalogForm(kind));
    const [proposing, setProposing] = useState(false);
    const [proposeError, setProposeError] = useState(null);

    const [myProposals, setMyProposals] = useState([]);
    const [proposalsLoading, setProposalsLoading] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => { setDebounced(search); setPage(1); }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    // Fresh state every time the modal opens.
    useEffect(() => {
        if (open) {
            setTab('browse'); setSearch(''); setDebounced(''); setPage(1);
            setSelected(new Set()); setLastResult(null);
            setProposeForm(emptyCatalogForm(kind)); setProposeError(null);
        }
    }, [open, kind]);

    const loadMaster = useCallback(async () => {
        setLoading(true);
        try {
            const axiosInstance = createAuthedAxios();
            const params = new URLSearchParams({ page, limit: PAGE_SIZE, status: 'active' });
            if (debounced) params.set('search', debounced);
            const { data } = await axiosInstance.get(`${baseURL}/master-catalog/${kind}?${params}`);
            setMasterItems(data.items || []);
            setCount(data.count || 0);
        } catch (err) {
            toast.warn(err?.response?.data?.message || t('pick.load_failed', 'Could not load the master catalog'));
        } finally {
            setLoading(false);
        }
    }, [kind, page, debounced, t]);

    useEffect(() => { if (open && tab === 'browse') loadMaster(); }, [open, tab, loadMaster]);

    const loadMyProposals = useCallback(async () => {
        setProposalsLoading(true);
        try {
            const axiosInstance = createAuthedAxios();
            const { data } = await axiosInstance.get(`${baseURL}/catalog/proposals?kind=${kind}`);
            setMyProposals(data.proposals || []);
        } catch (err) {
            toast.warn(err?.response?.data?.message || t('pick.proposals_failed', 'Could not load your proposals'));
        } finally {
            setProposalsLoading(false);
        }
    }, [kind, t]);

    useEffect(() => { if (open && tab === 'sent') loadMyProposals(); }, [open, tab, loadMyProposals]);

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handlePick = async () => {
        if (!selected.size) return;
        setPicking(true);
        try {
            const axiosInstance = createAuthedAxios();
            const { data } = await axiosInstance.post(`${baseURL}/catalog/${kind}/pick`, { ids: [...selected] });
            setLastResult(data);
            setSelected(new Set());
            if (data.picked?.length) {
                toast.success(t('pick.picked_n', { count: data.picked.length, defaultValue: `${data.picked.length} added to your list` }));
                onPicked?.(data);
            } else if (data.skipped?.length) {
                toast.info(t('pick.all_skipped', 'Everything selected is already in your list'));
            }
            if (data.failed?.length) {
                toast.warn(t('pick.some_failed', { count: data.failed.length, defaultValue: `${data.failed.length} could not be added — picking them again is safe` }));
            }
        } catch (err) {
            toast.warn(err?.response?.data?.message || t('pick.pick_failed', 'Could not add the items'));
            // Some rows may have landed before the failure — refresh the host
            // list so whatever WAS added becomes visible.
            onPicked?.(null);
        } finally {
            setPicking(false);
        }
    };

    const handlePropose = async () => {
        setProposeError(null);
        const requiredMissing = def.fields.filter((f) => f.required && String(proposeForm[f.key] ?? '').trim() === '');
        if (requiredMissing.length) {
            setProposeError({ message: t('pick.fill_required', { fields: requiredMissing.map((f) => f.label).join(', '), defaultValue: `Please fill: ${requiredMissing.map((f) => f.label).join(', ')}` }) });
            return;
        }
        const data = {};
        for (const f of def.fields) {
            const v = proposeForm[f.key];
            if (f.type === 'checkbox') data[f.key] = Boolean(v);
            else if (String(v ?? '').trim() !== '') data[f.key] = f.type === 'number' ? Number(v) : String(v).trim();
        }
        setProposing(true);
        try {
            const axiosInstance = createAuthedAxios();
            await axiosInstance.post(`${baseURL}/catalog/${kind}/proposals`, data);
            toast.success(t('pick.proposed', 'Sent for review — you\'ll see the decision under "Sent"'));
            setProposeForm(emptyCatalogForm(kind));
            setTab('sent');
        } catch (err) {
            const body = err?.response?.data;
            if (body?.code === 'ALREADY_IN_MASTER') {
                setProposeError({
                    message: t('pick.already_in_master', 'The master catalog already has this — pick it from Browse instead.'),
                    masterId: body.masterId,
                });
            } else {
                setProposeError({ message: body?.message || t('pick.propose_failed', 'Could not send the proposal') });
            }
        } finally {
            setProposing(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
    const statusStyles = useMemo(() => ({
        pending: 'bg-amber-100 text-amber-700 border-amber-300',
        approved: 'bg-green-100 text-green-700 border-green-300',
        rejected: 'bg-gray-200 text-gray-500 border-gray-300',
    }), []);

    if (!open || !def) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pick-from-master-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="border-b border-gray-200 p-4 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center">
                            <Library size={18} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800" data-testid="pick-from-master-title">
                                {t('pick.title', { label: def.labelPlural, defaultValue: `Pick ${def.labelPlural} from Master` })}
                            </h2>
                            <p className="text-[11px] text-gray-400">
                                {t('pick.subtitle', 'Curated world catalog — picked items land in your list with your own pricing.')}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors" data-testid="pick-from-master-close">
                        <X size={22} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="px-4 pt-3 flex gap-2 shrink-0">
                    {[
                        { id: 'browse', label: t('pick.tab_browse', 'Browse'), Icon: Search },
                        { id: 'propose', label: t('pick.tab_propose', 'Propose new'), Icon: Send },
                        { id: 'sent', label: t('pick.tab_sent', 'Sent'), Icon: Inbox },
                    ].map((tabDef) => (
                        <button
                            key={tabDef.id}
                            onClick={() => setTab(tabDef.id)}
                            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors duration-200 ${tab === tabDef.id ? 'border-[#4ABDBD] text-[#2D8A8A] font-semibold bg-[#4ABDBD]/5' : 'border-transparent text-gray-500 hover:text-[#4ABDBD] cursor-pointer'}`}
                            data-testid={`pick-from-master-tab-${tabDef.id}`}
                        >
                            <tabDef.Icon size={15} /> {tabDef.label}
                        </button>
                    ))}
                </div>

                {/* Body */}
                <div className="p-4 overflow-y-auto flex-1">
                    {tab === 'browse' && (
                        <>
                            <div className="relative group mb-3">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={18} />
                                <input
                                    type="text"
                                    placeholder={t('pick.search', def.searchHint)}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-sm text-gray-700"
                                    data-testid="pick-from-master-search"
                                />
                            </div>

                            {lastResult?.skipped?.length > 0 && (
                                <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
                                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                                    <span>
                                        {t('pick.skipped_note', { count: lastResult.skipped.length, defaultValue: `${lastResult.skipped.length} skipped — already in your list.` })}
                                    </span>
                                </div>
                            )}

                            {loading ? (
                                <div className="space-y-2" data-testid="pick-from-master-skeleton">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <div key={i} className="border border-gray-200 rounded-lg p-3 animate-pulse">
                                            <div className="h-3.5 bg-gray-200 rounded w-1/2 mb-2"></div>
                                            <div className="h-3 bg-gray-100 rounded w-1/3"></div>
                                        </div>
                                    ))}
                                </div>
                            ) : masterItems.length === 0 ? (
                                <div className="text-center py-10">
                                    <Library size={36} className="mx-auto text-gray-300 mb-3" />
                                    <p className="text-sm text-gray-500">
                                        {debounced
                                            ? t('pick.none_found', 'Nothing in the master matches — propose it?')
                                            : t('pick.none_yet', 'The master catalog is empty for now')}
                                    </p>
                                    {debounced && (
                                        <button
                                            onClick={() => setTab('propose')}
                                            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm border-2 border-[#3AA8A8] text-[#3AA8A8] rounded-lg hover:bg-[#3AA8A8] hover:text-white transition-colors duration-300"
                                        >
                                            <Send size={14} /> {t('pick.tab_propose', 'Propose new')}
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-2" data-testid="pick-from-master-list">
                                    {masterItems.map((item) => {
                                        const id = item[def.idField];
                                        const isSelected = selected.has(id);
                                        return (
                                            <div
                                                key={id}
                                                onClick={() => toggleSelect(id)}
                                                className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-all duration-200 ${isSelected ? 'border-[#4ABDBD] bg-[#4ABDBD]/5 shadow-sm' : 'border-gray-200 hover:border-[#4ABDBD]/60'}`}
                                                data-testid={`pick-from-master-item-${id}`}
                                            >
                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors duration-200 ${isSelected ? 'bg-[#4ABDBD] border-[#4ABDBD]' : 'border-gray-300'}`}>
                                                    {isSelected && <Check size={14} className="text-white" />}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-semibold text-gray-800 line-clamp-1">{item.name}</p>
                                                    <p className="text-xs text-[#2D8A8A] line-clamp-1">{catalogSubtitle(kind, item)}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Pager */}
                            {!loading && count > PAGE_SIZE && (
                                <div className="flex items-center justify-center gap-3 mt-3 text-sm text-gray-500">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                        className="p-1.5 rounded-lg border border-gray-200 hover:border-[#4ABDBD] hover:text-[#4ABDBD] transition-colors disabled:opacity-40"
                                        data-testid="pick-from-master-prev"
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <span>{page} / {totalPages}</span>
                                    <button
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages}
                                        className="p-1.5 rounded-lg border border-gray-200 hover:border-[#4ABDBD] hover:text-[#4ABDBD] transition-colors disabled:opacity-40"
                                        data-testid="pick-from-master-next"
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {tab === 'propose' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <p className="sm:col-span-2 text-xs text-gray-500 -mb-1">
                                {t('pick.propose_hint', 'Missing from the master? Describe it — the platform team reviews and adds it for every hospital. You can keep using your private copy meanwhile.')}
                            </p>
                            {proposeError && (
                                <div className="sm:col-span-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
                                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                                    <span>
                                        {proposeError.message}
                                    </span>
                                </div>
                            )}
                            {def.fields.map((f) => (
                                <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        {t(`mastercatalog.field_${f.key}`, f.label)}
                                        {f.required && <span className="text-red-500 ml-0.5">*</span>}
                                    </label>
                                    {f.type === 'select' ? (
                                        <select
                                            value={proposeForm[f.key] ?? ''}
                                            onChange={(e) => setProposeForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all"
                                            data-testid={`pick-propose-field-${f.key}`}
                                        >
                                            <option value="">{t('mastercatalog.not_set', '— not set —')}</option>
                                            {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : f.type === 'textarea' ? (
                                        <textarea
                                            rows={2}
                                            value={proposeForm[f.key] ?? ''}
                                            onChange={(e) => setProposeForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                                            maxLength={500}
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent resize-none transition-all"
                                            placeholder={f.placeholder || ''}
                                            data-testid={`pick-propose-field-${f.key}`}
                                        />
                                    ) : f.type === 'checkbox' ? (
                                        <label className="inline-flex items-center gap-2 cursor-pointer select-none py-1.5">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(proposeForm[f.key])}
                                                onChange={(e) => setProposeForm((prev) => ({ ...prev, [f.key]: e.target.checked }))}
                                                className="w-4 h-4 accent-[#4ABDBD]"
                                                data-testid={`pick-propose-field-${f.key}`}
                                            />
                                            <span className="text-sm text-gray-600">{t('mastercatalog.yes', 'Yes')}</span>
                                        </label>
                                    ) : (
                                        <input
                                            type={f.type === 'number' ? 'number' : 'text'}
                                            value={proposeForm[f.key] ?? ''}
                                            min={f.min} max={f.max} step={f.step}
                                            onChange={(e) => setProposeForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                                            maxLength={120}
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all"
                                            placeholder={f.placeholder || ''}
                                            data-testid={`pick-propose-field-${f.key}`}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === 'sent' && (
                        proposalsLoading ? (
                            <div className="space-y-2">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i} className="border border-gray-200 rounded-lg p-3 animate-pulse">
                                        <div className="h-3.5 bg-gray-200 rounded w-1/2 mb-2"></div>
                                        <div className="h-3 bg-gray-100 rounded w-1/4"></div>
                                    </div>
                                ))}
                            </div>
                        ) : myProposals.length === 0 ? (
                            <div className="text-center py-10">
                                <Inbox size={36} className="mx-auto text-gray-300 mb-3" />
                                <p className="text-sm text-gray-500">{t('pick.no_proposals', 'You haven\'t proposed anything yet')}</p>
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="pick-from-master-sent-list">
                                {myProposals.map((p) => (
                                    <div key={p.proposalId} className="border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-800 line-clamp-1">{p.payload?.name}</p>
                                            <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                                                <span className="font-mono">{p.proposalId}</span>
                                                {p.created_at && <span>{new Date(p.created_at).toLocaleDateString()}</span>}
                                                {p.reviewNote && <span className="italic">“{p.reviewNote}”</span>}
                                            </div>
                                        </div>
                                        <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold capitalize shrink-0 ${statusStyles[p.status] || statusStyles.rejected}`}>
                                            {t(`proposals.${p.status}`, p.status)}
                                        </span>
                                        {p.status === 'approved' && (
                                            <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 bg-gray-50 p-4 flex gap-3 shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                        data-testid="pick-from-master-cancel"
                    >
                        {t('mastercatalog.close', 'Close')}
                    </button>
                    {tab === 'browse' && (
                        <button
                            onClick={handlePick}
                            disabled={!selected.size || picking}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="pick-from-master-submit"
                        >
                            {picking && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>}
                            {selected.size
                                ? t('pick.add_n', { count: selected.size, defaultValue: `Add ${selected.size} to my list` })
                                : t('pick.select_items', 'Select items to add')}
                        </button>
                    )}
                    {tab === 'propose' && (
                        <button
                            onClick={handlePropose}
                            disabled={proposing}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="pick-propose-submit"
                        >
                            {proposing && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>}
                            {t('pick.send_proposal', 'Send for review')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PickFromMasterModal;
